import { Button, Input, Popover, PopoverContent, PopoverTrigger, cn } from "@portal/ui";
import { ChevronDown, Globe, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { EMOJI_CATEGORIES, EMOJI_CHOICES, type EmojiCategory } from "./icon-catalog";
import {
  EMOJI_KEYWORDS,
  FAVICON_VALUE,
  ICON_CHOICES,
  IconMark,
  iconOf,
  recommendedIcons,
  type IconChoice,
} from "./ServiceIcon";

/**
 * タブの並び。URL に合うものを「おすすめ」として先頭に、次にブランドのアイコン、
 * 続けて絵文字の種類(ユーザー要望 2026-09-16)。おすすめは URL がないときは出さない。
 */
/** 「サイトのアイコン」(そのリンク先の favicon。新しいリンクの既定。2026-10-08) */
const FAVICON_TITLE = "サイトのアイコン";

const BASE_TABS = ["サービス", ...EMOJI_CATEGORIES] as const;
const RECOMMENDED = "おすすめ";
type Tab = (typeof BASE_TABS)[number] | typeof RECOMMENDED;

interface Item {
  value: string;
  title: string;
  icon: IconChoice | string;
}

const brandItems: Item[] = ICON_CHOICES.map((c) => ({ value: c.value, title: c.title, icon: c }));

/**
 * URL のドメインから機械的に選んだおすすめ。ブランドのアイコンの後に、連想する絵文字を並べる。
 */
function recommendedItems(url: string): Item[] {
  const brands = recommendedIcons(url).map((c) => ({ value: c.value, title: c.title, icon: c }));
  let host = "";
  try {
    host = url === "" ? "" : new URL(url).hostname.toLowerCase();
  } catch {
    host = "";
  }
  const names = new Set(
    Object.entries(EMOJI_KEYWORDS)
      .filter(([label]) => host.includes(label))
      .flatMap(([, titles]) => titles),
  );
  const emoji = EMOJI_CHOICES.filter((c) => names.has(c.title)).map((c) => ({
    value: c.value,
    title: c.title,
    icon: c.value,
  }));
  return [...brands, ...emoji];
}
const emojiItems = (category: EmojiCategory): Item[] =>
  EMOJI_CHOICES.filter((c) => c.category === category).map((c) => ({
    value: c.value,
    title: c.title,
    icon: c.value,
  }));

/** アイコンの選択。いま選んでいるアイコンのボタンを押すと一覧が出る(ユーザー要望 2026-09-16)。 */
export function IconPicker({
  value,
  onChange,
  id,
  url = "",
}: {
  value: string;
  onChange: (icon: string) => void;
  id?: string;
  /** おすすめを出すために使う。空ならおすすめは出ない */
  url?: string;
}) {
  const [open, setOpen] = useState(false);
  const isFavicon = value === FAVICON_VALUE;
  const current = isFavicon ? null : iconOf(value);
  const title = isFavicon
    ? FAVICON_TITLE
    : [...ICON_CHOICES, ...EMOJI_CHOICES].find((c) => c.value === value)?.title;
  return (
    // ダイアログの中でもポップオーバーの中をスクロールできるよう modal にする
    // (ダイアログ側のスクロールロックが効いてしまうため)
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className="w-fit gap-2"
          aria-label={`アイコンを選ぶ(いまは ${title ?? "未選択"})`}
        >
          {isFavicon ? <Globe aria-hidden="true" className="size-5 text-muted-foreground" /> : null}
          {current ? <IconMark icon={current} className="size-5 text-[18px]" /> : null}
          <span className="text-xs text-muted-foreground">{title ?? "選ぶ"}</span>
          <ChevronDown aria-hidden="true" className="size-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      {/* 下だと入力欄が隠れるので右に出す(ユーザー要望 2026-09-16)。狭い画面では自動で収まる側に回る */}
      <PopoverContent side="right" align="start" className="w-88 p-2">
        <IconList
          value={value}
          url={url}
          onChange={(v) => {
            onChange(v);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * アイコンの一覧。Windows の絵文字ピッカーのように、1 つのスクロール領域に種類ごとの
 * 見出しを並べ、タブでその位置へ飛ぶ。タブは見えている種類が選ばれた状態になる。
 */
export function IconList({
  value,
  url,
  onChange,
  showFavicon = true,
  onClear,
}: {
  value: string;
  url: string;
  onChange: (icon: string) => void;
  /** 「サイトのアイコン」を出すか(リンク先の無いメモでは出さない。2026-10-15)。 */
  showFavicon?: boolean;
  /** 渡すと、先頭に「アイコンを外す」を出す(メモのアイコン)。 */
  onClear?: () => void;
}) {
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const sections = useMemo(() => {
    const base = BASE_TABS.map((tab) => ({
      tab: tab as Tab,
      items: tab === "サービス" ? brandItems : emojiItems(tab),
    }));
    const recommended = recommendedItems(url);
    return [
      ...(recommended.length > 0 ? [{ tab: RECOMMENDED as Tab, items: recommended }] : []),
      ...base,
    ];
  }, [url]);
  const [active, setActive] = useState<Tab>(sections[0]!.tab);
  const q = query.trim().toLowerCase();
  const shown = q
    ? sections
        .map((s) => ({ ...s, items: s.items.filter((i) => i.title.toLowerCase().includes(q)) }))
        .filter((s) => s.items.length > 0)
    : sections;

  // 見えている種類をタブに反映する
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const onScroll = () => {
      const tops = [...list.querySelectorAll<HTMLElement>("[data-icon-section]")];
      // 一番下まで来たら最後の種類。それ以外は、見出しがスクロール位置を超えていない最後の種類
      const bottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
      const seen = tops.filter((el) => el.offsetTop - list.offsetTop <= list.scrollTop + 1);
      const el = bottom ? tops.at(-1) : (seen.at(-1) ?? tops[0]);
      const tab = el?.dataset["iconSection"] as Tab | undefined;
      if (tab) setActive(tab);
    };
    onScroll();
    list.addEventListener("scroll", onScroll, { passive: true });
    return () => list.removeEventListener("scroll", onScroll);
  }, [shown.length]);

  const jumpTo = (tab: Tab) => {
    const list = listRef.current;
    const target = list?.querySelector<HTMLElement>(`[data-icon-section="${tab}"]`);
    // スクロール領域の中だけを動かす(ダイアログ全体が動かないように)
    if (list && target) list.scrollTop = target.offsetTop - list.offsetTop;
  };

  return (
    <div className="flex flex-col gap-2">
      {/* 検索窓はフォームの入力欄と区別できるよう、虫眼鏡付きの薄い背景にする */}
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          role="searchbox"
          aria-label="アイコンを検索"
          placeholder="アイコンを検索"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="rounded-full border-transparent bg-muted pl-8"
        />
      </div>
      {onClear && value !== "" && (
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm hover:bg-accent"
        >
          <X aria-hidden="true" className="size-4 text-muted-foreground" />
          <span className="font-medium">アイコンを外す</span>
        </button>
      )}
      {/* 先頭に「サイトのアイコン」(リンク先の favicon を自動で使う。新しいリンクの既定) */}
      {showFavicon && (
        <button
          type="button"
          onClick={() => onChange(FAVICON_VALUE)}
          aria-pressed={value === FAVICON_VALUE}
          className={cn(
            "flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm hover:bg-accent",
            value === FAVICON_VALUE && "border-foreground bg-accent",
          )}
        >
          <Globe aria-hidden="true" className="size-4 text-muted-foreground" />
          <span className="flex flex-col">
            <span className="font-medium">{FAVICON_TITLE}</span>
            <span className="text-xs text-muted-foreground">
              リンク先のサイトのアイコンを、自動で使います
            </span>
          </span>
        </button>
      )}
      {!q && (
        <div className="flex flex-wrap gap-0.5" role="tablist" aria-label="アイコンの種類">
          {sections.map(({ tab }) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={active === tab}
              onClick={() => jumpTo(tab)}
              className={cn(
                "rounded-md px-2 py-1 text-xs",
                active === tab
                  ? "bg-accent font-medium text-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
            >
              {tab}
            </button>
          ))}
        </div>
      )}
      <div
        ref={listRef}
        className="max-h-56 overflow-y-auto rounded-md border px-2"
        role="radiogroup"
        aria-label="アイコン"
      >
        {shown.map((s) => (
          <section key={s.tab} data-icon-section={s.tab}>
            {/* 見出しは上端に貼り付ける。左右にはみ出させて、下のアイコンが脇から見えないようにする */}
            <h4 className="sticky top-0 z-10 -mx-2 bg-background px-2 py-1.5 text-xs font-medium text-muted-foreground">
              {s.tab}
            </h4>
            <div className="flex flex-wrap gap-1 pb-2">
              {s.items.map((i) => (
                <button
                  key={i.value}
                  type="button"
                  role="radio"
                  aria-checked={value === i.value}
                  aria-label={i.title}
                  title={i.title}
                  onClick={() => onChange(i.value)}
                  className={cn(
                    "flex size-9 items-center justify-center rounded-md border",
                    value === i.value
                      ? "border-foreground bg-accent"
                      : "border-transparent hover:bg-accent",
                  )}
                >
                  <IconMark icon={i.icon} className="size-5 text-[18px]" />
                </button>
              ))}
            </div>
          </section>
        ))}
        {shown.length === 0 && (
          <p className="py-2 text-xs text-muted-foreground">該当するアイコンはありません。</p>
        )}
      </div>
    </div>
  );
}
